import type { DataChartStyleProps } from './datachartStyleRegistry'
import './AirQualityCard.css'

export type AirQualityCardVariant = 'default' | 'glass' | 'dense'
type AirMetric = {
  id: string
  label: string
  value: number
  unit: string
  status: 'good' | 'moderate' | 'warning'
}

const METRICS: AirMetric[] = [
  { id: 'aqi', label: 'AQI', value: 42, unit: '', status: 'good' },
  { id: 'pm25', label: 'PM2.5', value: 11, unit: 'ug/m3', status: 'good' },
  { id: 'pm10', label: 'PM10', value: 24, unit: 'ug/m3', status: 'moderate' },
  { id: 'co2', label: 'CO2', value: 612, unit: 'ppm', status: 'moderate' },
  { id: 'tvoc', label: 'TVOC', value: 213, unit: 'ppb', status: 'moderate' },
  { id: 'hcho', label: 'HCHO', value: 38, unit: 'ppb', status: 'good' },
  { id: 'temperature', label: 'Temperature', value: 24.5, unit: '°C', status: 'good' },
  { id: 'humidity', label: 'Humidity', value: 58, unit: '%', status: 'good' },
]

const STATUS_LABEL: Record<AirMetric['status'], string> = {
  good: 'Good',
  moderate: 'Moderate',
  warning: 'Warning',
}
const AIR_QUALITY_PRIORITY_IDS = ['temperature', 'humidity'] as const
const CLIMATE_METRIC_IDS = new Set(['temperature', 'humidity'])
const METRIC_ICON: Partial<Record<AirMetric['id'], string>> = {
  temperature: 'thermostat',
  humidity: 'water_drop',
}

export function AirQualityCardStyle01(_props: DataChartStyleProps) {
  return <AirQualityCardBase variant="default" {..._props} />
}

export function AirQualityCardBase(_props: DataChartStyleProps & { variant?: AirQualityCardVariant }) {
  void _props
  const variant = _props.variant ?? 'default'
  const aqi = METRICS.find((item) => item.id === 'aqi') ?? METRICS[0]
  const metricsWithoutAqi = METRICS.filter((item) => item.id !== 'aqi')
  const firstRowMetrics = AIR_QUALITY_PRIORITY_IDS.map((id) => metricsWithoutAqi.find((item) => item.id === id)).filter(
    (item): item is AirMetric => Boolean(item)
  )
  const remainingMetrics = metricsWithoutAqi.filter((item) => !AIR_QUALITY_PRIORITY_IDS.includes(item.id as (typeof AIR_QUALITY_PRIORITY_IDS)[number]))
  const orderedMetrics = [...firstRowMetrics, ...remainingMetrics]

  return (
    <div
      className={`air-quality-card air-quality-card--${variant}`}
      data-css-slot="cardFrame"
      data-css-slot-label="Card Frame"
    >
      <div className="air-quality-card__header">
        <div>
          <div className="air-quality-card__title">Air Quality Card</div>
          <div className="air-quality-card__subtitle">即時空氣品質總覽</div>
        </div>
        <div className={`air-quality-card__badge is-${aqi.status}`}>{STATUS_LABEL[aqi.status]}</div>
      </div>

      <div className="air-quality-card__hero">
        <span className="air-quality-card__hero-label">AQI</span>
        <strong className="air-quality-card__hero-value" data-css-slot="metricValue" data-css-slot-label="Metric Value">
          {aqi.value}
        </strong>
      </div>

      <div className="air-quality-card__grid">
        {orderedMetrics.map((item) => (
          <div
            key={item.id}
            className={`air-quality-card__item${CLIMATE_METRIC_IDS.has(item.id) ? ` is-climate is-${item.id}` : ''}`}
          >
            <div className="air-quality-card__item-top">
              <span className="air-quality-card__item-label-wrap">
                {METRIC_ICON[item.id] ? (
                  <span className="material-symbols-rounded air-quality-card__item-icon" aria-hidden>
                    {METRIC_ICON[item.id]}
                  </span>
                ) : null}
                <span className="air-quality-card__item-label">{item.label}</span>
              </span>
              <span className={`air-quality-card__item-status is-${item.status}`}>{STATUS_LABEL[item.status]}</span>
            </div>
            <div className="air-quality-card__item-value">
              {item.value.toLocaleString('en-US')}
              {item.unit ? <small>{item.unit}</small> : null}
            </div>
          </div>
        ))}
      </div>

      <div className="air-quality-card__footer">
        <span className="air-quality-card__live-dot" />
        Updated 1m ago
      </div>
    </div>
  )
}
